import { getSupabase } from '@/lib/supabase'
import { openWorkspaceDb } from '@/offline/db'
import { pushOutbox, type PushSummary } from '@/offline/sync'
import { supabaseTransport } from '@/offline/transport'
import { pullChanges } from '@/offline/pull'
import type { OutboxOperation } from '@/offline/types'

const TABLE_BY_TYPE: Record<string, string> = {
  device: 'devices',
  sale: 'sales',
  payment: 'payments',
  contact: 'contacts',
  expense: 'expenses',
  category: 'categories',
}

/** Outbox operations the server rejected as a same-revision conflict (drafts preserved). */
export async function listConflicts(workspaceId: string): Promise<OutboxOperation[]> {
  const db = openWorkspaceDb(workspaceId)
  return db.outbox.filter((o) => o.last_error === 'conflict').toArray()
}

/**
 * Resolve a conflict by keeping the LOCAL draft: re-send it as an update against the
 * server's CURRENT revision (fetched live), so it applies on top rather than colliding.
 */
export async function resolveKeepMine(workspaceId: string, op: OutboxOperation): Promise<PushSummary> {
  const db = openWorkspaceDb(workspaceId)
  const table = TABLE_BY_TYPE[op.entity_type]
  if (!table) throw new Error(`Unknown entity type ${op.entity_type}`)

  const { data, error } = await getSupabase()
    .from(table)
    .select('revision')
    .eq('id', op.entity_id)
    .maybeSingle()
  if (error) throw error

  const serverRevision = (data?.revision as number | undefined) ?? null
  await db.outbox.update(op.operation_id, { expected_revision: serverRevision, last_error: null })
  return pushOutbox(db, supabaseTransport())
}

/** Resolve a conflict by accepting the SERVER version: drop the local draft op and re-pull. */
export async function resolveUseServer(workspaceId: string, op: OutboxOperation): Promise<void> {
  const db = openWorkspaceDb(workspaceId)
  await db.outbox.delete(op.operation_id)
  await pullChanges(workspaceId)
}
